import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    DocumentSourceProvider,
    RequestedPermission,
    SelectedClosingFolder,
    SharepointConnection,
} from './sharepoint-folders.types';

/**
 * REV-21057 — SharePoint "Ordner hinzufügen" folder-selection pure view.
 *
 * Dumb, presentational component (Angular 14): it renders the document-source
 * picker (SharePoint / Google Drive / manueller Upload), the Microsoft 365
 * connection state, and the list of already-selected closing folders.
 *
 * It is a PURE VIEW: only @Input/@Output. It owns NO store, NO HTTP, NO business
 * logic and performs NO Microsoft/OAuth calls. Every action is surfaced as an
 * @Output the parent (orchestrator) reacts to; the view never mutates its inputs.
 */
@Component({
    selector: 'aumico-integration-sharepoint-folders',
    templateUrl: 'sharepoint-folders.component.html',
    styleUrls: ['sharepoint-folders.component.scss'],
})
export class SharepointFoldersComponent {
    /** The document-source tiles to render. */
    @Input() public providers: DocumentSourceProvider[] = [];

    /** Which tile is currently selected (the SharePoint panel only shows for 'sharepoint'). */
    @Input() public selectedProviderId: DocumentSourceProvider['id'] | null = 'sharepoint';

    /** State of the Microsoft 365 connection. */
    @Input() public connection: SharepointConnection = { status: 'disconnected' };

    /** Folders already picked for the closing (only meaningful when connected). */
    @Input() public folders: SelectedClosingFolder[] = [];

    /**
     * The scopes listed on the in-app consent card (status === 'awaiting-consent').
     * Supplied by the parent — the view neither knows nor derives the requested scopes.
     */
    @Input() public requestedPermissions: RequestedPermission[] = [];

    /** i18next key of the message shown under "Ordner hinzufügen" when the picker cannot open. */
    @Input() public addFolderErrorKey: string | null = null;

    /** A different provider tile was chosen. */
    @Output() public selectProvider = new EventEmitter<DocumentSourceProvider['id']>();

    /** "Mit Microsoft 365 verbinden" was clicked. */
    @Output() public connect = new EventEmitter<void>();

    /**
     * "Zugriff erlauben" on the in-app consent card was clicked. The parent — not this
     * view — starts the Microsoft sign-in/consent flow, so the popup it opens still
     * happens inside the user gesture that produced this emission.
     */
    @Output() public allowConsent = new EventEmitter<void>();

    /** "Abbrechen" on the in-app consent card was clicked. */
    @Output() public cancelConsent = new EventEmitter<void>();

    /** "Ordner hinzufügen" was clicked. */
    @Output() public addFolder = new EventEmitter<void>();

    /** The remove (×) control on a folder row was clicked; emits the folder id. */
    @Output() public removeFolder = new EventEmitter<string>();

    /**
     * AC B3 — the user may switch 'include subfolders' off (or back on) AFTER picking.
     * Emits the binding id plus the NEW value; the view never mutates its own inputs.
     */
    @Output() public toggleSubfolders = new EventEmitter<{ id: string; includeSubfolders: boolean }>();

    public onSelectProvider(provider: DocumentSourceProvider): void {
        if (!provider.enabled) {
            return;
        }
        this.selectProvider.emit(provider.id);
    }

    public onConnect(): void {
        this.connect.emit();
    }

    public onAllowConsent(): void {
        this.allowConsent.emit();
    }

    public onCancelConsent(): void {
        this.cancelConsent.emit();
    }

    public onAddFolder(): void {
        this.addFolder.emit();
    }

    public onRemoveFolder(id: string): void {
        this.removeFolder.emit(id);
    }

    public onToggleSubfolders(id: string, includeSubfolders: boolean): void {
        this.toggleSubfolders.emit({ id, includeSubfolders });
    }
}
